import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PaymentRequestFormMeta,
  PaymentRequestSpentItem,
  createPaymentRequests,
  getPaymentRequestFormMeta,
  getPaymentRequestSpent,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { SearchableSelect } from '../components/SearchableSelect';
import { displayContractSumDraft, formatContractSumDisplay, formatDateShort, normalizeContractSumDraft } from '../utils/table';
import { formatSignedSum, getPaymentControlLight, spentLookupKey } from '../utils/paymentRequestFinance';
import { notifyPaymentApprovalsChanged } from '../utils/paymentApprovalsEvents';
import '../styles/DataPage.css';
import './PaymentRequestPage.css';

const ROW_COUNT = 30;

interface RequestRow {
  task_id: string;
  planfact_article: string;
  note: string;
  amount: string;
}

type SpentKey = string;

function spentKey(taskId: string, article: string): SpentKey {
  return spentLookupKey(taskId, article);
}

function emptyRows(): RequestRow[] {
  return Array.from({ length: ROW_COUNT }, () => ({
    task_id: '',
    planfact_article: '',
    note: '',
    amount: '',
  }));
}

function parseAmount(value: string): number | null {
  const trimmed = value.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) {
    return null;
  }
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
}

function isRowComplete(row: RequestRow): boolean {
  const amount = parseAmount(row.amount);
  return Boolean(row.task_id.trim() && row.planfact_article.trim() && amount !== null && amount > 0);
}

function sanitizeRows(
  rows: RequestRow[],
  allowedTaskIds: Set<string>,
  allowedArticles: Set<string>,
): RequestRow[] {
  return rows.map((row) => ({
    ...row,
    task_id: allowedTaskIds.has(row.task_id) ? row.task_id : '',
    planfact_article: allowedArticles.has(row.planfact_article) ? row.planfact_article : '',
  }));
}

const PaymentRequestPage: React.FC = () => {
  const { can } = useAuth();
  const canCreate = can('OUTCOME_REQUEST', 'CREATE');

  const [meta, setMeta] = useState<PaymentRequestFormMeta | null>(null);
  const [requestorId, setRequestorId] = useState<number | null>(null);
  const [rows, setRows] = useState<RequestRow[]>(() => emptyRows());
  const [spentByKey, setSpentByKey] = useState<Record<SpentKey, PaymentRequestSpentItem>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadMeta = useCallback(async (nextRequestorId?: number | null): Promise<void> => {
    if (!canCreate) {
      setLoading(false);
      setError('Недостаточно прав для создания заявок.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getPaymentRequestFormMeta(nextRequestorId ?? undefined);
      setMeta({
        ...data,
        planfact_articles: data.planfact_articles ?? [],
        tasks: data.tasks ?? [],
        requestors: data.requestors ?? [],
      });
      setRequestorId(data.requestor_id);
      const allowedTaskIds = new Set((data.tasks ?? []).map((task) => task.task_id));
      const allowedArticles = new Set(data.planfact_articles ?? []);
      setRows((prev) => sanitizeRows(prev, allowedTaskIds, allowedArticles));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [canCreate]);

  useEffect(() => {
    void loadMeta();
  }, [loadMeta]);

  const updateRow = (index: number, patch: Partial<RequestRow>): void => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    setSuccess(null);
  };

  const pairSignature = rows.map((row) => `${row.task_id.trim()}\t${row.planfact_article.trim()}`).join('\n');

  const spentPairs = useMemo(() => {
    const seen = new Set<SpentKey>();
    const items: Array<{ task_id: string; planfact_article: string }> = [];
    pairSignature.split('\n').forEach((line) => {
      const [taskId = '', article = ''] = line.split('\t');
      if (!taskId || !article) {
        return;
      }
      const key = spentKey(taskId, article);
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      items.push({ task_id: taskId, planfact_article: article });
    });
    return items;
  }, [pairSignature]);

  useEffect(() => {
    if (!canCreate || spentPairs.length === 0) {
      setSpentByKey({});
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const items = await getPaymentRequestSpent(spentPairs);
          if (cancelled) {
            return;
          }
          const next: Record<SpentKey, PaymentRequestSpentItem> = {};
          items.forEach((item) => {
            next[spentKey(item.task_id, item.planfact_article)] = item;
          });
          setSpentByKey(next);
        } catch {
          if (!cancelled) {
            setSpentByKey({});
          }
        }
      })();
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [canCreate, spentPairs]);

  const taskOptions = useMemo(
    () =>
      (meta?.tasks ?? []).map((task) => ({
        value: task.task_id,
        label: task.short_name,
      })),
    [meta?.tasks],
  );

  const articleOptions = useMemo(
    () =>
      (meta?.planfact_articles ?? []).map((article) => ({
        value: article,
        label: article,
      })),
    [meta?.planfact_articles],
  );

  const requestorOptions = useMemo(
    () =>
      (meta?.requestors ?? []).map((person) => ({
        value: String(person.id),
        label: person.full_name,
      })),
    [meta?.requestors],
  );

  const totalAmount = useMemo(
    () =>
      rows.reduce((sum, row) => {
        const amount = parseAmount(row.amount);
        return amount !== null && amount > 0 ? sum + amount : sum;
      }, 0),
    [rows],
  );

  const filledCount = useMemo(
    () => rows.filter(isRowComplete).length,
    [rows],
  );

  const handleRequestorChange = (value: string): void => {
    const nextId = Number(value);
    if (!Number.isFinite(nextId) || nextId === requestorId) {
      return;
    }
    setRequestorId(nextId);
    void loadMeta(nextId);
  };

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    setSuccess(null);

    const items = rows
      .filter(isRowComplete)
      .map((row) => ({
        task_id: row.task_id.trim(),
        planfact_article: row.planfact_article.trim(),
        amount: parseAmount(row.amount) as number,
        note: row.note.trim() || null,
      }));

    if (items.length === 0) {
      setError('Заполните хотя бы одну строку (задача, статья и сумма).');
      return;
    }

    setSaving(true);
    try {
      const result = await createPaymentRequests(items, requestorId ?? undefined);
      setSuccess(`Сохранено заявок: ${result.created_count}`);
      setRows(emptyRows());
      await loadMeta(requestorId);
      notifyPaymentApprovalsChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !meta) {
    return <div className="data-page payment-request-page"><div className="loading">Загрузка...</div></div>;
  }

  return (
    <div className="data-page payment-request-page">
      <div className="page-header-row">
        <div>
          <h1>Заявка на выдачу денежных средств</h1>
          <p className="page-subtitle">Заполните до 30 строк и нажмите «Запросить»</p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="create-button"
            onClick={() => void handleSubmit()}
            disabled={!canCreate || saving}
          >
            {saving ? 'Сохранение...' : 'Запросить'}
          </button>
        </div>
      </div>

      {meta ? (
        <div className="payment-request-meta">
          <div className="payment-request-meta__left">
            <div>
              <span className="payment-request-meta__label">Дата</span>
              <span>{formatDateShort(meta.request_date)}</span>
            </div>
            <div className="payment-request-meta__requestor">
              <span className="payment-request-meta__label">Запрашивающий</span>
              {meta.can_select_requestor ? (
                <SearchableSelect
                  className="payment-request-meta-picker"
                  options={requestorOptions}
                  value={requestorId != null ? String(requestorId) : String(meta.requestor_id)}
                  onChange={handleRequestorChange}
                  disabled={!canCreate || saving || requestorOptions.length === 0}
                  placeholder="Поиск сотрудника…"
                  emptyLabel="— выберите —"
                  allowEmpty={false}
                  ariaLabel="Запрашивающий"
                />
              ) : (
                <span>{meta.requestor_name}</span>
              )}
            </div>
            <div>
              <span className="payment-request-meta__label">Согласующий</span>
              <span>{meta.approver_name || '— не указан —'}</span>
            </div>
          </div>
          <div className="payment-request-meta__total">
            <span className="payment-request-meta__label">Сумма</span>
            <span className="payment-request-meta__total-value">{formatContractSumDisplay(totalAmount)}</span>
          </div>
        </div>
      ) : null}

      {error ? <div className="error-message">{error}</div> : null}
      {success ? <div className="success-message">{success}</div> : null}

      <div className="table-scroll">
        <table className="data-table payment-request-table">
          <colgroup>
            <col className="payment-request-table__col-id" />
            <col className="payment-request-table__col-task" />
            <col className="payment-request-table__col-article" />
            <col className="payment-request-table__col-note" />
            <col className="payment-request-table__col-amount" />
            <col className="payment-request-table__col-info" />
            <col className="payment-request-table__col-info" />
            <col className="payment-request-table__col-info" />
            <col className="payment-request-table__col-control" />
          </colgroup>
          <thead>
            <tr>
              <th>ID</th>
              <th>Задача</th>
              <th>Статья</th>
              <th>Примечание</th>
              <th>Сумма</th>
              <th className="payment-request-table__info-head">План</th>
              <th className="payment-request-table__info-head">Потрачено</th>
              <th className="payment-request-table__info-head">Дельта</th>
              <th>Контроль</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const info = spentByKey[spentKey(row.task_id, row.planfact_article)];
              const hasPair = Boolean(row.task_id.trim() && row.planfact_article.trim());
              const planValue = info?.plan ?? null;
              const spentValue = hasPair ? info?.spent ?? null : null;
              const deltaValue = planValue != null && spentValue != null
                ? spentValue - planValue
                : null;
              const requested = parseAmount(row.amount) ?? 0;
              const light = getPaymentControlLight(planValue, deltaValue, requested);
              return (
                <tr key={index}>
                  <td className="payment-request-table__id" title={row.task_id || undefined}>
                    <span className="payment-request-table__cell-text">{row.task_id || '—'}</span>
                  </td>
                  <td>
                    <SearchableSelect
                      className="payment-request-picker"
                      options={taskOptions}
                      value={row.task_id}
                      onChange={(value) => updateRow(index, { task_id: value })}
                      disabled={!canCreate || saving || taskOptions.length === 0}
                      placeholder="Поиск задачи…"
                      emptyLabel="— выберите —"
                      ariaLabel="Задача"
                    />
                  </td>
                  <td>
                    <SearchableSelect
                      className="payment-request-picker"
                      options={articleOptions}
                      value={row.planfact_article}
                      onChange={(value) => updateRow(index, { planfact_article: value })}
                      disabled={!canCreate || saving || articleOptions.length === 0}
                      placeholder="Поиск статьи…"
                      emptyLabel="— выберите —"
                      ariaLabel="Статья"
                    />
                  </td>
                  <td>
                    <textarea
                      value={row.note}
                      onChange={(e) => updateRow(index, { note: e.target.value })}
                      disabled={!canCreate || saving}
                      placeholder="Примечание"
                      rows={2}
                      title={row.note || undefined}
                    />
                  </td>
                  <td className="payment-request-table__amount">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={displayContractSumDraft(row.amount)}
                      onChange={(e) => updateRow(index, { amount: normalizeContractSumDraft(e.target.value) })}
                      disabled={!canCreate || saving}
                      placeholder="0"
                      autoComplete="off"
                    />
                  </td>
                  <td className="payment-request-table__info col-num">
                    {planValue == null ? '—' : formatContractSumDisplay(planValue)}
                  </td>
                  <td className="payment-request-table__info col-num">
                    {spentValue == null ? '—' : formatContractSumDisplay(-spentValue)}
                  </td>
                  <td className="payment-request-table__info col-num">
                    {deltaValue == null ? '—' : formatSignedSum(deltaValue)}
                  </td>
                  <td className="payment-request-table__control">
                    <span
                      className={`payment-request-table__light payment-request-table__light--${light}`}
                      title={
                        light === 'green'
                          ? 'В пределах плана'
                          : light === 'red'
                            ? 'Перетрата относительно плана'
                            : 'Плана нет'
                      }
                      aria-label="Контроль"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="page-subtitle payment-request-footer">
        Заполнено строк: {filledCount} / {ROW_COUNT}
        {(meta?.planfact_articles?.length ?? 0) === 0
          ? ' · Нет статей РАСХОДЫ, доступных по ролям запрашивающего'
          : null}
        {(meta?.tasks?.length ?? 0) === 0 ? ' · Нет задач для выбора' : null}
      </p>
    </div>
  );
};

export default PaymentRequestPage;
